import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { FaUserPlus } from 'react-icons/fa';
import { FaXmark } from 'react-icons/fa6';
import classNames from 'classnames';
import Avatar from './Avatar';
import Button from './Button';
import { getCommunityMembers } from '../../services/communityService';

const MIN_SEARCH = 5;

type ParticipantPickerProps = {
  communityId?: number;
  selected: number[];
  setSelected: (ids: number[]) => void;
  minSearch?: number;
  placeholder?: string;
  disabled?: (member: any) => boolean;
  disabledMessage?: string;
};

export default function ParticipantPicker({
  communityId,
  selected,
  setSelected,
  minSearch = MIN_SEARCH,
  placeholder,
  disabled = () => false,
  disabledMessage,
}: ParticipantPickerProps) {
  const { t } = useTranslation();
  const [members, setMembers] = useState<any[]>([]);
  const [query, setQuery] = useState('');

  // Modal state
  const [adding, setAdding] = useState(false);
  const [modalSelected, setModalSelected] = useState<number[]>([]);
  const [modalSearch, setModalSearch] = useState('');
  const [showSelected, setShowSelected] = useState(false);

  useEffect(() => {
    if (!communityId) return;
    getCommunityMembers(communityId, 200)
      .then((m) => {
        const normalized = m.map((member: any) => ({
          ...member,
          id: member.memberId,
        }));
        setMembers(normalized);
      })
      .catch((err) => console.error('Failed to load community members', err));
  }, [communityId]);

  useEffect(() => {
    if (!adding) return;
    setModalSelected(selected);
    setModalSearch('');
    setShowSelected(false);
  }, [adding, selected]);

  const add = (id: number) => setSelected([...selected, id]);
  const remove = (id: number) => setSelected(selected.filter((s) => s !== id));

  // Modal helpers
  const isInModalSelected = (id: number) => modalSelected.includes(id);
  const handleModalSelect = (id: number) => {
    if (isInModalSelected(id)) {
      setModalSelected((prev) => prev.filter((s) => s !== id));
    } else {
      setModalSelected((prev) => [...prev, id]);
    }
  };
  const handleModalSelectAll = () => {
    const nonDisabled = members.filter((m) => !disabled(m));
    const allSelected = nonDisabled.every((m) => isInModalSelected(m.id));
    if (allSelected) {
      // Deselect all non-disabled members
      const nonDisabledIds = nonDisabled.map((m) => m.id);
      setModalSelected(modalSelected.filter((id) => !nonDisabledIds.includes(id)));
    } else {
      // Select all non-disabled members
      const toAdd = nonDisabled.map((m) => m.id).filter((id) => !modalSelected.includes(id));
      setModalSelected([...modalSelected, ...toAdd]);
    }
  };

  const filteredInline = members
    .filter((m) => !selected.includes(m.id))
    .filter((m) => `${m.firstName} ${m.lastName}`.toLowerCase().includes(query.toLowerCase()));

  const filteredModal = members
    .filter((m) => !showSelected || modalSelected.includes(m.id))
    .filter(
      (m) =>
        m.firstName.toLowerCase().includes(modalSearch.toLowerCase()) ||
        m.lastName.toLowerCase().includes(modalSearch.toLowerCase()) ||
        `${m.firstName} ${m.lastName}`.toLowerCase().includes(modalSearch.toLowerCase()),
    );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-full flex-wrap items-center gap-4">
        <div className="flex h-fit w-fit flex-col items-center gap-2 hover:opacity-80">
          <Button
            key="inviteButton"
            variant="primary"
            onClick={() => setAdding(true)}
            className="h-[56px] w-[56px] items-center justify-center rounded-full">
            <FaUserPlus className="h-5 w-5" />
          </Button>
          <span onClick={() => setAdding(true)} className="cursor-pointer font-medium text-primary-900">
            {t('page.createTask.invite')}
          </span>
        </div>

        {/* Selected chips */}
        <div className="flex flex-wrap gap-2">
          {selected.map((id) => {
            const member = members.find((m) => m.id === id);
            if (!member) return null;
            return (
              <div key={id} className="relative">
                <div className="flex h-fit w-fit flex-col gap-2">
                  <Avatar
                    src={member.picture}
                    alias={`${member.firstName}+${member.lastName}`}
                    className="h-[56px] w-[56px] rounded-full bg-secondary-200 text-2xl font-semibold text-secondary-50"
                  />
                  <p className="max-w-[80px] text-center font-semibold">{member.firstName}</p>
                </div>
                <button
                  type="button"
                  onClick={() => remove(id)}
                  className="absolute -right-1 -top-1 rounded-full bg-secondary p-1 text-secondary-50 shadow-lg transition-opacity hover:opacity-90">
                  <FaXmark className="size-6" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Inline search for quick add */}
      {members.length > minSearch && (
        <div className="mt-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder || t('component.modal.participantPicker.searchPlaceholder')}
            className="w-full rounded-lg border-none !bg-secondary-50 p-2"
          />
        </div>
      )}

      {/* Inline dropdown */}
      {query !== '' && filteredInline.length > 0 && (
        <div className="absolute z-30 mt-1 max-h-44 w-full overflow-auto rounded-md bg-secondary p-2 shadow-md">
          {filteredInline.slice(0, 8).map((m) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(ev) => ev.preventDefault()}
              onClick={() => add(m.id)}
              className="flex w-full items-center gap-2 rounded px-2 py-1 hover:bg-secondary-200">
              <Avatar src={m.picture} alias={`${m.firstName} ${m.lastName}`} className="h-6 w-6" />
              <span className="truncate">{`${m.firstName} ${m.lastName}`}</span>
            </button>
          ))}

          {filteredInline.length === 0 && (
            <div className="text-secondary-600 px-2 py-2 text-sm">
              {t('component.modal.participantPicker.noMembersFound')}
            </div>
          )}
        </div>
      )}

      {/* Selection Modal */}
      <Transition appear show={adding} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setAdding(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="w-full max-w-[600px] transform overflow-hidden rounded-2xl bg-secondary p-6 text-left align-middle shadow-xl transition-all">
                  <div className="mb-4 flex items-center gap-2">
                    <h3 className="text-2xl font-semibold">{t('component.modal.selectionChange.title')}</h3>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    {members.length > 1 && (
                      <input
                        className="rounded-lg border-none !bg-secondary-50 p-2"
                        placeholder={t('component.modal.participantPicker.searchPlaceholder')}
                        value={modalSearch}
                        onChange={(e) => setModalSearch(e.target.value)}
                      />
                    )}

                    <p
                      onClick={() => setShowSelected(!showSelected)}
                      className="cursor-pointer whitespace-nowrap text-[13px] font-semibold">
                      {t(`form.userData.show${showSelected ? 'All' : 'Selected'}`)}{' '}
                      {!showSelected && `(${modalSelected.length})`}
                    </p>
                    <p
                      onClick={handleModalSelectAll}
                      className="cursor-pointer whitespace-nowrap text-[13px] font-semibold text-primary-300">
                      {t('form.userData.selectAll')}
                    </p>
                  </div>

                  <div className="mt-4 max-h-[400px] overflow-y-auto">
                    {filteredModal.length === 0 && (
                      <p className="text-secondary-600">{t('component.modal.participantPicker.noMembersFound')}</p>
                    )}
                    {filteredModal.map((m) => {
                      const isDisabled = disabled(m);
                      const isSelected = modalSelected.includes(m.id);
                      return (
                        <div
                          key={m.id}
                          onClick={() => !isDisabled && handleModalSelect(m.id)}
                          className={classNames(
                            'mb-2 flex cursor-pointer items-center justify-between rounded-[8px] border border-secondary-200 px-3 py-2 transition-all',
                            { 'bg-secondary-200': isSelected, 'border-opacity-20': isDisabled },
                          )}>
                          <div className="flex items-center gap-2">
                            <Avatar
                              src={m.picture}
                              alias={`${m.firstName} ${m.lastName}`}
                              className={classNames('h-9 w-9', { 'opacity-20': isDisabled })}
                            />
                            <p className={classNames('font-medium', { 'opacity-20': isDisabled })}>
                              {`${m.firstName} ${m.lastName}`.replace('+', ' ')}
                            </p>
                            {isDisabled && disabledMessage && (
                              <div className="bg-danger rounded-[6px] bg-opacity-50 p-0.5 text-[10px] font-semibold text-secondary-50">
                                {disabledMessage}
                              </div>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            className={isDisabled ? 'opacity-20' : ''}
                            checked={isSelected}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                            readOnly
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 flex w-full justify-end gap-3">
                    <Button variant="secondary" onClick={() => setAdding(false)}>
                      {t('general.cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        setSelected(modalSelected);
                        setAdding(false);
                      }}>
                      {t('general.save')}
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
